import { inject, injectable } from 'inversify';
import { Logger } from '../models/logger';
import { MetricEventName } from '../types/metric-events';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { ApiErrorReport, ApiErrorSink } from '../utils/api-error-report';
import { configureRequestIdHeader } from '../utils/request-id';
import { ApiConfig } from './api-config';
import { HttpClient } from './http-client';
import { LightHttp } from './light-http.service';
import { SettingsService } from './settings.service';
import { SocketJSService } from './socketjs.service';
import { WebSocketService } from './websocket.service';

interface MetricEvent {
    event: MetricEventName;
    attributes: Record<string, string | number | boolean>;
    timestamp: string;
    pageUrl: string;
    wsId: string;
    sessionId: string;
    platform: string;
}

@injectable()
export class MetricsService implements ApiErrorSink {
    /**
     * Convert-option changes observed on this page load. CONVERT_START reports it
     * so "did they touch the knobs before converting" is one query, without
     * plumbing option state through the converter component.
     */
    public optionChangesThisPage = 0;

    private readonly logger = Logger.getLogger('MetricsService');

    private buffer: MetricEvent[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * True once the user downloaded anything this session. Set by the
     * FILE_DOWNLOAD emit site; read by the downloads page to gate the grow
     * pitch to the post-download moment (owner decision 2026-08-31).
     */
    public downloadedThisSession = false;
    private readonly MAX_BUFFER_SIZE = 10;
    private readonly FLUSH_INTERVAL_MS = 5000;

    /**
     * When each file upload was dispatched for processing, so CONVERT_COMPLETE /
     * CONVERT_ERROR can report a duration.
     *
     * It lives here rather than on FileUpload because the two ends of the
     * measurement are in different services (FileProcessService dispatches,
     * FileManagerService sees the terminal message) and a FileUpload is
     * serialised to localStorage — a wall-clock start time written there would
     * come back hours later, after a page reload, and produce a nonsense
     * duration. Entries are consumed on read, so the map cannot outgrow the
     * number of in-flight conversions.
     */
    private readonly convertStartedAt = new Map<string, number>();

    /**
     * Mark the per-file analogue of CONVERT_START. CONVERT_START itself is
     * emitted once per Convert CLICK (which may cover N files), so the
     * per-workflow duration has to be anchored where the individual upload is
     * actually dispatched. Decision 2026-09-02.
     */
    markConvertStart(fileUploadId: string) {
        if (!fileUploadId) return;
        this.convertStartedAt.set(fileUploadId, Date.now());
    }

    /** Elapsed ms since markConvertStart for this id, consuming the entry. */
    takeConvertStartMs(fileUploadId: string): number | null {
        if (!fileUploadId) return null;
        const startedAt = this.convertStartedAt.get(fileUploadId);
        if (startedAt == null) return null;
        this.convertStartedAt.delete(fileUploadId);
        return Date.now() - startedAt;
    }

    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(HttpClient) private http: HttpClient,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(SocketJSService) private socketJSService: SocketJSService,
        @inject(SettingsService) private settingsService: SettingsService,
    ) {
        // Self-registration, matching HelperService/AuthService/LoginService:
        // the HTTP clients cannot inject MetricsService (it injects HttpClient
        // and reaches LightHttp through WebSocketService -> SettingsService, so
        // the edge would close a real import cycle), so the provider registers
        // itself. Everything before this constructor runs — a few boot requests
        // — is not counted; that gap is accepted, not queued around.
        HttpClient.registerApiErrorSink(this);
        LightHttp.registerApiErrorSink(this);
        this.wireRequestIdHeader();
    }

    /**
     * `featureFlags.requestIdHeader` — DEFAULT OFF, and it must stay off until
     * nginx ships the CORS change.
     *
     * 2026-09-02: `X-Request-Id` is not a CORS-safelisted request header, so the
     * browser preflights any cross-origin call that carries it — and every one
     * of our API/edge calls is cross-origin (api./edge. subdomains). Until
     * `Access-Control-Allow-Headers` on the edge and api server blocks lists
     * `X-Request-Id`, EVERY preflight fails and the site stops working, not
     * degrades. The nginx drop (METRICS.md §7.2) has to land, be reloaded on
     * all 11 hosts, and be verified with an OPTIONS probe BEFORE this flag is
     * flipped on for anyone. `Access-Control-Expose-Headers` in the same drop
     * is what lets API_ERROR read the id back off a failed response.
     */
    private wireRequestIdHeader() {
        try {
            this.settingsService.featureFlags.subscribe(ff => {
                configureRequestIdHeader({
                    enabled: ff?.requestIdHeader === true,
                    sessionId: this.socketJSService.sessionId,
                });
            });
        } catch { /* a flag we cannot read stays off */ }
    }

    /**
     * API_ERROR — one event per failed backend attempt, from the two HTTP
     * clients' central error paths.
     *
     * Bounded by construction: the endpoint is a path TEMPLATE with ids
     * replaced, never a URL; the status is a number; nothing carries a message
     * body. `requestId` appears only once nginx exposes `X-Request-Id`
     * (METRICS.md §7.2) and is what joins a browser error to the Graylog line
     * that produced it.
     */
    reportApiError(report: ApiErrorReport) {
        this.emit(MetricEventName.API_ERROR, {
            endpoint: report.endpoint,
            method: report.method,
            status: report.status,
            ms: report.ms,
            ...(report.requestId ? { requestId: report.requestId } : {}),
        });
    }

    emit(event: MetricEventName, attributes: Record<string, string | number | boolean> = {}) {
        if (!BrowserUtils.hasWindow()) return;

        this.buffer.push({
            event,
            attributes,
            timestamp: new Date().toISOString(),
            pageUrl: window.location.pathname,
            wsId: this.wsService.getOrCreateStoredWSId(),
            sessionId: this.socketJSService.sessionId,
            platform: this.detectPlatform(),
        });

        if (this.buffer.length >= this.MAX_BUFFER_SIZE) {
            this.flush();
        } else if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => this.flush(), this.FLUSH_INTERVAL_MS);
        }
    }

    flush() {
        if (this.buffer.length === 0) return;
        const batch = this.buffer.splice(0);
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        try {
            // text/plain, matching the sendBeacon path: the endpoint's JSON
            // consumes-path 400s (Gson converter can't bind a JSON object to
            // the controller's String @RequestBody), so every application/json
            // flush was silently rejected. Beacon posts always worked.
            this.http.post(this.apiConfig.getMetricsEndpoint(), JSON.stringify({ events: batch }), {
                headers: { 'Content-Type': 'text/plain' },
            }).catch((e) => this.logger.error(e)); // bound: bare this.logger.error loses `this` and threw in the catch itself
        } catch { /* metrics are best-effort */ }
    }

    flushOnUnload() {
        if (this.buffer.length === 0) return;
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const payload = JSON.stringify({ events: this.buffer.splice(0) });
            navigator.sendBeacon(this.apiConfig.getMetricsEndpoint(), payload);
        } else {
            this.flush();
        }
    }

    private detectPlatform(): string {
        if (typeof window === 'undefined') return 'server';
        const w = window.innerWidth;
        if (w < 768) return 'mobile';
        if (w < 1024) return 'tablet';
        return 'desktop';
    }
}
